import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Sync as SyncIcon } from '@mui/icons-material';
import { authGet, authPost } from '../../services/apiService';

const SYNC_URL = '/api/customer-issues/vital-signs/l2-investigation-sync';
const POLL_MS = 2000;

// What a dry run shows: what a sync would do.
const DRY_ROWS = [
  ['adoTickets', 'Investigation tickets read from ADO (TSI area, any state)'],
  ['storedTickets', 'Investigations stored in the portal (any area)'],
  ['storedNotDeleted', 'Stored and not marked deleted'],
  ['storedOutsideTsiArea', 'Stored in another area or with no area'],
  ['toCreate', 'In ADO, not stored: would be created'],
  ['toRefresh', 'In both: would be refreshed'],
  ['differingFromAdo', 'Of those, stored data differs from ADO (stale)'],
  ['alreadyCurrent', 'Of those, already current'],
  ['toRestore', 'Stored as deleted but ADO has them: deleted flag would be cleared'],
  ['outsideTsiQuery', 'Stored, not listed by the TSI query, but still in ADO (other area or type): would be refreshed'],
  ['toMarkDeleted', 'Stored, gone from ADO and in its recycle bin: would be marked deleted'],
  ['missingUnconfirmed', 'Stored, gone from ADO but not in its recycle bin (maybe no access): left unchanged'],
  ['listedButUnreadable', 'Listed by ADO but could not be read: left unchanged']
];

// What a finished sync shows next to what it wrote: what it found before it wrote, with no "would".
const FOUND_ROWS = [
  ['adoTickets', 'Investigation tickets read from ADO (TSI area, any state)'],
  ['storedTickets', 'Investigations stored in the portal (any area)'],
  ['storedNotDeleted', 'Stored and not marked deleted'],
  ['storedOutsideTsiArea', 'Stored in another area or with no area'],
  ['toCreate', 'In ADO but not stored'],
  ['toRefresh', 'In ADO and stored (TSI query)'],
  ['differingFromAdo', 'Of those, stored data differed from ADO (was stale)'],
  ['alreadyCurrent', 'Of those, already current'],
  ['toRestore', 'Stored as deleted, but ADO has them'],
  ['outsideTsiQuery', 'Stored, not listed by the TSI query, but still in ADO (other area or type)'],
  ['toMarkDeleted', 'Stored, gone from ADO and in its recycle bin'],
  ['missingUnconfirmed', 'Stored, gone from ADO but not in its recycle bin (maybe no access): left unchanged'],
  ['listedButUnreadable', 'Listed by ADO but could not be read: left unchanged']
];

const APPLIED_ROWS = [
  ['created', 'Created'],
  ['updated', 'Refreshed'],
  ['errors', 'Failed to save'],
  ['notReturned', 'ADO did not return'],
  ['partialRead', 'Not saved: ADO read was incomplete (run the sync again)'],
  ['skippedNewerInPortal', 'Skipped (portal copy was newer)'],
  ['restored', 'Deleted flag cleared'],
  ['markedDeleted', 'Marked deleted'],
  ['deleteSkippedChanged', 'Not marked deleted: changed since the sync read it']
];

const NO_IDS = { ids: [], total: 0 };

const formatValue = (value) => (value == null || value === '' ? '(empty)' : String(value));

const CountTable = ({ rows, values }) => (
  <TableContainer>
    <Table size="small">
      <TableBody>
        {rows.map(([key, label]) => (
          <TableRow key={key}>
            <TableCell>{label}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>{values[key] ?? 0}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const IdList = ({ title, ids, total }) => (
  total > 0 ? (
    <Accordion disableGutters variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2">{title} ({total})</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
          {ids.join(', ')}{total > ids.length ? ` … and ${total - ids.length} more` : ''}
        </Typography>
      </AccordionDetails>
    </Accordion>
  ) : null
);

const stamp = (run) => (run.finishedAt ? new Date(run.finishedAt).toLocaleString() : '');

/** One line under the collapsed header: what the run came to, in numbers. */
const runSummary = (run) => {
  const { counts = {}, applied = {} } = run.result || {};
  if (run.dryRun) {
    return `${counts.toRefresh ?? 0} to refresh (${counts.differingFromAdo ?? 0} stale), ${counts.toCreate ?? 0} to create, `
      + `${counts.toMarkDeleted ?? 0} to mark deleted`;
  }
  return `${applied.created ?? 0} created, ${applied.updated ?? 0} refreshed, ${applied.markedDeleted ?? 0} marked deleted, `
    + `${applied.errors ?? 0} failed`;
};

const RunResult = ({ run }) => {
  const result = run.result;
  if (!result) return null;
  const { counts, lists, samples, guard, applied } = result;
  const dry = Boolean(result.dryRun);
  // A run saved before these lists existed has no entry for them.
  const unconfirmed = lists.unconfirmed || NO_IDS;
  const unreadable = lists.unreadable || NO_IDS;
  const outsideQuery = lists.outsideTsiQuery || lists.moved || { items: [], total: 0 };
  return (
    <Box>
      <Alert severity={dry ? 'info' : 'success'} sx={{ mb: 2 }}>
        {dry ? 'Dry run' : 'Sync'} finished {stamp(run)}.
        {dry ? ' Nothing was written.' : ' The changes below were written.'}
      </Alert>
      {guard && guard.skippedDeletes && <Alert severity="warning" sx={{ mb: 2 }}>{guard.skippedDeletes}</Alert>}

      {!dry && applied && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>What was written</Typography>
          <CountTable rows={APPLIED_ROWS} values={applied} />
          {applied.failures && applied.failures.length > 0 && (
            <Alert severity="error" sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Tickets that failed to save ({applied.errors})</Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 0.5 }}>
                {applied.failures.map((failure) => (
                  <Typography key={failure.id} variant="caption" sx={{ display: 'block' }}>
                    #{failure.id}: {failure.error}
                  </Typography>
                ))}
              </Box>
              {applied.errors > applied.failures.length && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  … and {applied.errors - applied.failures.length} more (see the server log).
                </Typography>
              )}
            </Alert>
          )}
          {applied.zendeskLinks && (
            <Alert severity={applied.zendeskLinks.error ? 'warning' : 'info'} sx={{ mt: 1 }}>
              Zendesk Support ID references: read {applied.zendeskLinks.fetched} tickets, {applied.zendeskLinks.withValue} with
              a value, {applied.zendeskLinks.updated} stored, {applied.zendeskLinks.imported} imported,
              {' '}{applied.zendeskLinks.cleared} cleared.
              {applied.zendeskLinks.warning ? ` ${applied.zendeskLinks.warning}` : ''}
              {applied.zendeskLinks.error ? ` Failed: ${applied.zendeskLinks.error}` : ''}
            </Alert>
          )}
        </>
      )}

      <Typography variant="subtitle2" sx={{ mt: dry ? 0 : 2, mb: 0.5 }}>
        {dry ? 'What a sync would do' : 'What the sync found before it wrote'}
      </Typography>
      <CountTable rows={dry ? DRY_ROWS : FOUND_ROWS} values={counts} />

      {samples && samples.differing.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
            {dry ? 'Stored tickets that differ from ADO' : 'Tickets that were stale before the sync'}
            {' '}(first {samples.differing.length} of {counts.differingFromAdo})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ADO ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Stored → ADO</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {samples.differing.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>#{row.id}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>{row.title}</TableCell>
                    <TableCell>
                      {Object.entries(row.fields).map(([field, [from, to]]) => (
                        <Typography key={field} variant="caption" sx={{ display: 'block' }}>
                          <strong>{field}</strong>: {formatValue(from)} → {formatValue(to)}
                        </Typography>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <IdList title={dry ? 'Would be created' : 'Created'} ids={lists.create.ids} total={lists.create.total} />
        <IdList
          title={dry ? 'Deleted flag would be cleared' : 'Deleted flag cleared'}
          ids={lists.restore.ids}
          total={lists.restore.total}
        />
        <IdList
          title={`Outside the TSI query but still in ADO (type, area)${dry ? ': would be refreshed' : ': refreshed'}`}
          ids={outsideQuery.items.map((row) => `${row.id} (${row.type || '?'}, ${row.areaPath || '?'})`)}
          total={outsideQuery.total}
        />
        <IdList
          title={dry ? 'Confirmed deleted in ADO: would be marked deleted' : 'Confirmed deleted in ADO: marked deleted'}
          ids={lists.markDeleted.ids}
          total={lists.markDeleted.total}
        />
        <IdList title="Gone from ADO but not confirmed deleted: left unchanged" ids={unconfirmed.ids} total={unconfirmed.total} />
        <IdList title="Listed by ADO but unreadable: left unchanged" ids={unreadable.ids} total={unreadable.total} />
      </Box>
    </Box>
  );
};

/**
 * Admin: one-time sync of the Investigation (L2) tickets in the portal's copy of ADO. A dry run reads ADO and the
 * portal and writes nothing; the real run refreshes every Investigation in the TSI area, marks the ones deleted in ADO
 * as deleted and stores their Zendesk Support ID references. The run happens in the background; this screen polls it.
 */
const InvestigationSyncAdmin = () => {
  const [run, setRun] = useState(null);
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const [expanded, setExpanded] = useState(false); // the last run's details start collapsed
  const wasRunning = useRef(false);
  const inFlight = useRef(false); // one status request at a time, so a slow poll is not overtaken by the next
  const mounted = useRef(true);

  // Set true on every mount: StrictMode mounts, unmounts and remounts in development.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const response = await authGet(SYNC_URL, null, 'json', { useCache: false });
      if (!response.success) throw new Error(response.message || 'Failed to load the Investigation sync status');
      if (!mounted.current) return;
      setRun(response.run);
      setScope(response.scope);
    } catch (error) {
      if (mounted.current) setMessage({ severity: 'error', text: error.message || 'Failed to load the Investigation sync status' });
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll on a fixed interval while a run is active. Each poll is independent, so one failed request does not end polling.
  const running = Boolean(run && run.status === 'running');
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [running, load]);

  // A run that finishes while this page is open shows its details; one from an earlier visit stays collapsed.
  useEffect(() => {
    if (running) {
      wasRunning.current = true;
    } else if (wasRunning.current && run && run.status !== 'running') {
      wasRunning.current = false;
      setExpanded(true);
    }
  }, [running, run]);

  const start = async () => {
    setConfirmOpen(false);
    setStarting(true);
    setMessage(null);
    try {
      const response = await authPost(SYNC_URL, { dryRun });
      if (!response.success) throw new Error(response.message || 'Failed to start the Investigation sync');
      setRun(response.run);
    } catch (error) {
      setMessage({ severity: 'error', text: error.message || 'Failed to start the Investigation sync' });
    } finally {
      setStarting(false);
    }
  };

  const handleClick = () => {
    if (dryRun) start();
    else setConfirmOpen(true);
  };

  const progress = run && run.progress;

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <SyncIcon sx={{ color: '#2E7D32' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Investigation tickets sync (one time)</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Brings the portal's copy of the L2 Investigation tickets{scope ? ` (${scope.workItemType} in ${scope.areaPath})` : ''} in
        line with ADO. It refreshes every Investigation in that area, in any state, and every other stored
        Investigation ADO still has; marks as deleted only the ones ADO's recycle bin confirms are deleted; and
        stores the Zendesk Support ID references. A dry run reads ADO and the portal and changes nothing: run it
        first and check the counts. Only one run can be active at a time.
      </Typography>

      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={<Checkbox checked={dryRun} onChange={(event) => setDryRun(event.target.checked)} disabled={running || starting} />}
          label="Dry run (make no changes)"
        />
        <Button
          variant="contained"
          color={dryRun ? 'primary' : 'warning'}
          startIcon={starting || running ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
          onClick={handleClick}
          disabled={loading || starting || running}
        >
          {dryRun ? 'Run dry run' : 'Run sync'}
        </Button>
      </Box>

      {running && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {run.dryRun ? 'Dry run' : 'Sync'} running: {progress && progress.phase}
            {progress && progress.total > 0 ? ` (${progress.processed} of ${progress.total})` : ''}
          </Typography>
          <LinearProgress
            variant={progress && progress.total > 0 ? 'determinate' : 'indeterminate'}
            value={progress && progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0}
          />
        </Box>
      )}

      {run && run.status === 'failed' && (
        <Alert severity="error" sx={{ mt: 2 }}>
          The last {run.dryRun ? 'dry run' : 'sync'} failed{run.finishedAt ? ` (${new Date(run.finishedAt).toLocaleString()})` : ''}: {run.error}
        </Alert>
      )}

      {run && run.status === 'completed' && (
        <Accordion
          expanded={expanded}
          onChange={(_event, open) => setExpanded(open)}
          disableGutters
          variant="outlined"
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Last {run.dryRun ? 'dry run' : 'sync'}, finished {stamp(run)}
              </Typography>
              <Typography variant="caption" color="text.secondary">{runSummary(run)}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <RunResult run={run} />
          </AccordionDetails>
        </Accordion>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Run the Investigation sync?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This writes to the portal's work items: it refreshes the stored Investigations, marks the ones ADO's
            recycle bin confirms are deleted as deleted, and stores the Zendesk Support ID references. Run a dry run
            first and check the counts. The sync cannot be undone from this screen.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={start} color="warning" variant="contained">Run sync</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default InvestigationSyncAdmin;
